import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import {
  Box,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography as Muitypo,
  useTheme,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { LicenseManager } from 'ag-grid-enterprise';
import {
  CellClickedEvent,
  CellRendererSelectorFunc,
  RowNode,
  GetRowIdParams,
  SelectionChangedEvent,
  IServerSideDatasource,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import './custom.css';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { gridOptions } from '../../utils/gridOptions';
import { Tabs, Select } from '../../components';
import { LoadingDialogue } from '../../components/Spinners';
import TextField from '../../components/TextField/TextField';
import { GLOBAL_USER } from '../../utils/constants';
import GridTopActions from './GridTopActions';
import AppliedFilters from './AppliedFilters';
import {
  getAllRowData,
  CellTypes,
  updateAssignmentType,
  getAllVehicles,
  getAllChangeHistory,
  getVehicleFilterValues,
  getAssignmentTypesCustomFieldDetails,
} from '../../services/vehicles';
import VehicleListLoader from './VehicleListLoader';
import useConfirm from '../../hooks/useConfirm';
import { usePermissions } from '../../hooks/usePermissions';
import useSnackbarNotifications from '../../hooks/useSnackbarNotifications';
import { RootState } from '../../redux/store';
import AGGridHoc from '../../hoc/AGGridHoc';
import { metaData } from './dummy';
import { ELocations, Vehicle } from '../../types';
import { BulkActionWarning, BulkActionLimit } from './ActionModal';
import { fetchData } from './FetchData';
// import { useColumnDefs } from './useColumnDefs';
import useFilters from '../../hooks/useFilters';
import ChangeHistory from '../ChangeHistory';
import { useQuickFilter } from '../../hooks/useQuickFilter'; // remove once change history ssrm changes are done.
import { useDataSource } from '../../hooks/useDataSource';
import groupingFn from '../../utils/grouping';
import { ReactComponent as CheckBoxTick } from '../../assets/icons/checkbox-checked.svg';
import { ReactComponent as CheckBoxUntick } from '../../assets/icons/checkbox-unchecked.svg';
import { useLocationPermissions } from '../../hooks/useLocationPermissions';

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE_KEY as string);

interface IActionList extends Vehicle {
  decision?: 'Rejected' | 'Approved';
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const IconCheckBoxTickedComponent = ({ data }: any) => {
  return (
    <Box display="flex" alignItems="center">
      <LightTooltip title="" placement="bottom">
        <CheckBoxTick />
      </LightTooltip>
    </Box>
  );
};

const IconCheckBoxNotTickedComponent = () => {
  return (
    <Box display="flex" alignItems="center">
      <LightTooltip title="" placement="bottom">
        <CheckBoxUntick />
      </LightTooltip>
    </Box>
  );
};

let bulkComment = '';
let selectedCategory = '';

const VehiclesGrid = () => {
  // declare
  let FakeServer: any;
  // const metaData = useMetaData(META_DATA_MODULES.vehicles, META_DATA_SUB_MODULES.vehiclesGrid);
  const { confirm } = useConfirm();
  const { getRole, hasPermissions } = usePermissions();
  const { hasLocationPermissions } = useLocationPermissions();
  const theme = useTheme();
  // use hooks
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarNotifications();
  const gridRef = useRef<AgGridReact>(null);
  // global states
  const appliedFilter = useSelector((state: RootState) => state.filter.appliedFilter);
  // local states
  const [filterModel, setFilterModel] = useState<{ [key: string]: any }>();
  const [decisionPending, setDecisionPending] = useState(false);
  const [gridApi, setGridApi] = useState<any>(null);
  const [toBulkActionEvent, setToBulkActionEvent] = useState<any>([]);
  const [toActionEvent, setToActionEvent] = useState<any>([]);
  const [assignmentTypes, setAssignmentTypes] = useState<any>({});
  const [bulkActionToggleOpen, setBulkActionToggleOpen] = useState(false);
  const perPage = 50;
  const [requestSelected, setRequestSelected] = useState(0);
  const [rowData, setRowData] = useState<any>();
  const [showSubCategory, setShowSubcategory] = useState(false);
  const [inputText, setInputText] = useState('');
  const [failedUpdates, setFailedUpdates] = useState<any>([]);
  const [bulkActionButtonVisibility, setBulkActionButtonVisibility] = useState(true);
  const [filteredVins, setFilteredVins] = useState([]);
  const [groupBy, setGroupBy] = useState<any>();
  const { getFilterData } = useFilters();
  const [filterData, setFilterData] = useState<any>();
  const { quickFilterParser, quickFilterMatcher, parseMultipleSearchText, multipleSearch } = useQuickFilter(); // remove once change history ssrm changes are done.
  const BulkAnchorRef = useRef<HTMLButtonElement | null>(null);
  const [paginationInfo, setPaginationInfo] = useState({
    sort: '',
    sortStatus: false,
    filter: '',
    filterOp: [],
  });
  const [uiHeadings, setUIHeadings] = useState([]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (pathname.includes('change-history')) {
      setActive(1);
    }
  }, [pathname]);

  // Local variable
  let reason = '';
  let tempFailedUpdates: any;
  let tempData: any = [];

  const rowClassRules = useMemo(() => {
    return {
      failed: (params: any) => {
        return params.data?.failedUpdates?.length > 0;
      },
    };
  }, []);

  useEffect(() => {
    gridRef?.current?.api?.setFilterModel(appliedFilter?.filters);
  }, [appliedFilter?.filters]);

  const refreshGrid = () => gridRef?.current?.api.refreshServerSide();
  const areFiltersApplied = filterModel && !!Object.keys(filterModel).length;
  const gridHeight = areFiltersApplied ? '225pt' : '190pt';

  let tempGroupedData: any = [];

  const datasource = {
    getRows(params: any) {
      console.log(JSON.stringify(params.request, null, 1));
      const { startRow, endRow, rowGroupCols, sortModel, groupKeys, filterModel: paramFilter } = params.request;
      /* Pagination */
      const page = endRow && endRow / 50;

      /* Sorting */
      const sort = `${sortModel[0]?.sort}:${sortModel[0]?.colId}`;
      const sortStatus = sortModel?.length > 0;
      setPaginationInfo((prevData) => ({
        ...prevData,
        sort,
        sortStatus,
      }));

      /* Sidebar Filter */
      let tempFilter = {
        temp: { values: [], filterType: 'set', filter: '', operator: '', conditions: [], filterTo: '' },
      };
      const tempFilterKeys: any = [];
      const filterOpCheck: any = [];
      if (paramFilter) {
        tempFilter = paramFilter;
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of Object.entries(tempFilter)) {
          console.log(key, value);
          if (key === 'batchNumber') {
            const repl = (s: any) => {
              return s.toString().replace(/\d+/g, function (m: any) {
                return '000000000'.substring(m.length - 1) + m;
              });
            };

            const temp = `batchNumber:${repl(value?.filter)},${repl(value?.filterTo)}`;
            filterOpCheck.push('batchNumber:RANGE');
            tempFilterKeys.push(temp);
          } else {
            if (value && value.filter) {
              console.log(value.filter);
              const temp = `${key}:${value.filter}`;
              tempFilterKeys.push(temp);
            }
            if (value && value?.operator) {
              const conditions = value?.conditions?.map((a: any) => a?.filter);
              const temp = `${key}:${conditions}`;
              filterOpCheck.push(`${key}:${value?.operator}`);
              tempFilterKeys.push(temp);
            }
            if (value && value?.values?.length > 0) {
              let tempKeyValues: any = value.values;
              const dateFormat = (date: any) => {
                const d = new Date(date);
                /* eslint-disable prefer-template */
                date = [d.getFullYear(), ('0' + (d.getMonth() + 1)).slice(-2), ('0' + d.getDate()).slice(-2)].join('-');
                return date;
              };
              if (key.includes('Date')) {
                tempKeyValues = value.values?.map((val) => dateFormat(val));
              }
              const temp = `${key}:${tempKeyValues}`;
              tempFilterKeys.push(temp);
            }
          }
        }
      }

      setPaginationInfo((prevData) => ({
        ...prevData,
        filter: tempFilterKeys,
        filterOp: filterOpCheck,
      }));

      /* Grouping */
      const grouping = rowGroupCols?.slice(-1)[0]?.field;
      let groupByData;
      if (groupKeys.length > 0) {
        const myArr = groupKeys[0]?.split('(')[0];
        groupByData = `${grouping}:${myArr}`;
      }
      let arr: any = [];
      if (grouping && arr.length > 0 && groupKeys.length === 0) {
        const numberOfItemsPerPage = 50;
        const start = (page - 1) * numberOfItemsPerPage + 1;
        const end = start + numberOfItemsPerPage;

        console.log(start, end);
        const tempNewAllRowData: any = tempGroupedData.slice(start, end);

        let arrTemp: any = [];

        if (grouping && groupKeys.length === 0) {
          arrTemp = groupingFn(grouping, tempNewAllRowData);
        }

        params.successCallback(arrTemp, tempGroupedData.length);
      } else {
        getAllRowData(50, page, sortStatus, sort, inputText, grouping, groupKeys, tempFilterKeys, filterOpCheck)
          .then((newAllRowData: any) => {
            const lastRow = newAllRowData?.totalData ? newAllRowData?.totalData : newAllRowData?.data?.length;
            arr = newAllRowData?.data.length > 0 ? newAllRowData?.data : [];
            if (grouping && page === 1) {
              tempGroupedData = newAllRowData?.data;
            }

            if (grouping && groupKeys.length === 0) {
              arr = groupingFn(grouping, newAllRowData?.data);
            }
            tempData = arr;
            setRowData(arr);
            params.successCallback(arr, lastRow);
          })
          .catch(() => {
            params.failCallback();
          });
      }
    },
  };

  const setHeadings = (columns: any) => {
    const headings = columns?.filter((column: any) => !column.hide)?.map((col: any) => col.field.replace('.', '_'));
    setUIHeadings(headings);
  };

  const onGridReady: any = (params: any) => {
    setGridApi(params);
    setHeadings(params?.api?.getColumnDefs());
    params.api.setServerSideDatasource(datasource);
  };

  let categoryOptions = [{ label: 'Select Category', value: 'Select  Category' }];
  let categoryOptionsNew =
    assignmentTypes &&
    assignmentTypes[0] &&
    assignmentTypes[0].option?.value?.forEach((ele: any) => categoryOptions.push({ label: ele, value: ele }));

  const handleBulkComment = (e: any) => {
    bulkComment = e.target.value;
  };

  const ActionInputModal = (
    <Stack py={1} spacing={2}>
      {/* {toBulkActionEvent.length > 0 && ( */}
      <Box
        p={1}
        mx={1}
        sx={{
          color: '#c3493b',
          display: 'flex',
          border: '1px solid #c3493b',
          borderRadius: 2,
          alignItems: 'center',
        }}
      >
        <Icon icon="charm:circle-warning" color="#c3493b" />
        <Muitypo variant="caption" ml={1}>
          Please be careful with bulk editing. This action cannot be reversed.
        </Muitypo>
      </Box>
      {/* )} */}
      <Stack>
        <Muitypo variant="caption" ml={1} sx={{ fontSize: '16px' }}>
          Would you like the changes the Category for the {toBulkActionEvent.length} vehicles below? Please confirm to
          make changes.
        </Muitypo>
      </Stack>
      <Stack>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ borderBottom: 0, color: '#000006', padding: '1px' }}>
                <Muitypo variant="body1">VIN</Muitypo>
              </TableCell>
              <TableCell sx={{ borderBottom: 0, color: '#000006', padding: '1px' }}>
                <Muitypo variant="body1">Category</Muitypo>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {toBulkActionEvent.map((value: any) => {
              return (
                <TableRow key={value._id}>
                  <TableCell sx={{ borderBottom: 0, fontSize: '16px', height: 20, padding: '1px' }}>
                    <Muitypo variant="caption">{value.vin}</Muitypo>
                  </TableCell>
                  <TableCell sx={{ borderBottom: 0, fontSize: '16px', height: 20, padding: '1px' }}>
                    <Muitypo variant="caption">
                      {value.category} - {value.subCategory}
                    </Muitypo>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Stack>
      <Stack direction="column" gap={1} alignItems="left">
        <Muitypo variant="body1">Category</Muitypo>
        <Select
          options={categoryOptions}
          height={theme.typography.pxToRem(40)}
          setSelectedOption={(o: any) => {
            selectedCategory = o;
          }}
        />
      </Stack>
      <Stack>
        <Muitypo variant="body1">Comment</Muitypo>
        <TextField placeholder="Enter comment" sx={{ fontSize: 12, height: 36 }} onChange={handleBulkComment} />
      </Stack>
    </Stack>
  );
  const ActionModal = (
    <Stack py={1} spacing={2}>
      <Stack>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ borderBottom: 0, color: '#000006', padding: '1px' }}>
                <Muitypo variant="body1">VIN</Muitypo>
              </TableCell>
              <TableCell sx={{ borderBottom: 0, color: '#000006', padding: '1px' }}>
                <Muitypo variant="body1">Category</Muitypo>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {toActionEvent.map((value: any) => {
              categoryOptions = [{ label: 'Select Category', value: 'Select  Category' }];
              categoryOptionsNew =
                assignmentTypes &&
                assignmentTypes[0] &&
                assignmentTypes[0].option?.value?.forEach((ele: any) =>
                  categoryOptions.push({ label: ele, value: ele })
                );
              return (
                <TableRow key={value?._id}>
                  <TableCell sx={{ borderBottom: 0, fontSize: '16px', height: 20, padding: '1px' }}>
                    <Muitypo variant="caption">{value?.vin}</Muitypo>
                  </TableCell>
                  <TableCell sx={{ borderBottom: 0, fontSize: '16px', height: 20, padding: '1px' }}>
                    <Muitypo variant="caption">
                      {value?.category} - {value?.subCategory}
                    </Muitypo>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Stack>
      <Stack direction="column" gap={1} alignItems="left">
        <Muitypo variant="body1">Category</Muitypo>
        <Select
          options={categoryOptions}
          height={theme.typography.pxToRem(40)}
          setSelectedOption={(o: any) => {
            selectedCategory = o;
            console.log('sele', selectedCategory);
          }}
        />
      </Stack>
      <Stack>
        <Muitypo variant="body1">Comment</Muitypo>
        <TextField placeholder="Enter comment" sx={{ fontSize: 12, height: 36 }} onChange={handleBulkComment} />
      </Stack>
    </Stack>
  );

  // handling action change - Single update
  const handleActionChangeType = async (event: React.MouseEvent<HTMLElement>) => {
    // const handleActionChangeType = async (event: CellClickedEvent<Vehicle, Vehicle['disabled']>) => {
    console.log('.............toActionEvent.........', toActionEvent);

    let isConfirmed: any = null;
    if (toActionEvent.length > 0) {
      isConfirmed = await confirm({
        title: 'Change Assignment Type',
        description: 'Please select the assignment type and confirm to make changes to the vehicle.',
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Confirm',
        confirmBody: ActionModal,
      });
    }
    if (isConfirmed) {
      setDecisionPending(true);

      const testSinglePldPayload =
        toActionEvent &&
        toActionEvent?.map((data: any) => ({
          previousCategory: data.category,
          previousSubCategory: data.subCategory,
          VIN: data.vin,
          batchNumber: data.batchNumber,
        }));
      testSinglePldPayload.forEach((ele: any) => {
        if (selectedCategory !== 'Select  Category ' && selectedCategory !== '') {
          const [first, ...rest] = selectedCategory.split('-');
          const re = rest.join('-');
          // eslint-disable-next-line prefer-destructuring
          ele.newCategory = first?.trim();
          // eslint-disable-next-line prefer-destructuring
          ele.newSubCategory = re?.trim();
        }
      });
      try {
        const ActionPayload = {
          bulk: false,
          requestor: GLOBAL_USER,
          comments: bulkComment,
          data: testSinglePldPayload,
        };

        const res = await updateAssignmentType(ActionPayload);
        if (res.statusCode !== 200) {
          if (!res) {
            showErrorSnackbar({ message: 'Something went wrong. Try again.' });
          } else {
            showErrorSnackbar({ message: res.message });
          }
        } else if (res.statusCode === 200) {
          showSuccessSnackbar({
            message: `The request has been successfully updated.`,
          });
        }
        // getAllVehicles(50, 1).then((freshRowData) => {
        //   setRowData(freshRowData);
        // });
        const newSelectedRows = rowData?.filter((newData: any) => toBulkActionEvent.includes(newData));
        const test = newSelectedRows.forEach((ele: any) => {
          if (selectedCategory !== 'Select  Category' && selectedCategory !== '') {
            if (ele.prevAssgnChange) {
              ele.prevAssgnChange.previousCategory = ele.category;
              ele.prevAssgnChange.previousSubCategory = ele.subCategory;
              ele.prevAssgnChange.comments = bulkComment;
            }

            const [first, ...rest] = selectedCategory.split('-');
            const re = rest.join('-');
            // eslint-disable-next-line prefer-destructuring
            ele.category = first;
            // eslint-disable-next-line prefer-destructuring
            ele.subCategory = re;
          }
          // if(bulkComment !== ''){
          //   ele.comments = bulkComment,
          // }
        });
        // gridRef.current!.api.applyTransaction({
        //   update: newSelectedRows,
        // });
        gridRef.current!.api.applyServerSideTransaction({
          update: newSelectedRows,
        });
        refreshGrid();
      } catch (error) {
        showErrorSnackbar({ message: 'Something went wrong. Try again.' });
      } finally {
        setDecisionPending(false);
        reason = '';
      }
      refreshGrid();
    } else {
      reason = '';
    }
  };

  const getFilterValuesAsync = async (params: any) => {
    const col = params.colDef.field;
    const data = await getVehicleFilterValues(col);
    return params.success(data.data);
  };

  const filterParamsTemp = {
    values: getFilterValuesAsync,
  };

  const batchNoFilterParamsTemp = {
    filterOptions: ['inRange'],
    maxNumConditions: 1,
  };

  const columnDefs = useMemo(
    () =>
      metaData?.columnDefs?.map(
        ({
          type,
          headerName,
          field,
          sortable,
          width,
          filter,
          hide,
          valueGetter,
          suppressFiltersToolPanel,
          suppressColumnsToolPanel,
          enableRowGroup,
        }: any) => {
          const cellRenderer = (CellTypes as any)[type];
          const cellRendererSelector: CellRendererSelectorFunc<Vehicle> = (params) => {
            if (field === 'disabled') {
              const isNotAvailable = params.data?.disabled === 'yes';
              return {
                component: cellRenderer,
                params: {
                  handleActionChangeType,
                  sx: {
                    border: isNotAvailable ? '1px solid #456BD9 !important' : '1px solid transparent !important',
                    outline: isNotAvailable ? '#456BD9' : 'none',
                    color: !isNotAvailable ? '#456BD9' : null,
                  },
                  disabled: isNotAvailable,
                },
              };
            }
            if (field === 'inTransitToLocation' && params.data?.inTransitToLocation !== undefined) {
              return {
                component:
                  params.data?.inTransitToLocation === 'true' || params.data?.inTransitToLocation.includes('true')
                    ? IconCheckBoxTickedComponent
                    : IconCheckBoxNotTickedComponent,
              };
            }
            return {
              component: cellRenderer,
            };
          };

          if (hasPermissions(['CHANGE_ASSIGNMENT_ACTION'])) {
            if (field === 'batchNumber') {
              return {
                headerName: 'Batch Numbers',
                field: 'batchNumber',
                filter: 'agNumberColumnFilter',
                headerCheckboxSelection: true,
                headerCheckboxSelectionCurrentPageOnly: true,
                headerCheckboxSelectionFilteredOnly: true,
                checkboxSelection: true,
                showDisabledCheckboxes: true,
                filterParams: batchNoFilterParamsTemp,
                // valueFormatter: batchNoValueFormatter,
              };
            }
          }

          if (field === 'euPortDate') {
            if (!hasLocationPermissions([ELocations.EUROPE])) {
              return {
                hide: true,
                filter: false,
                suppressColumnsToolPanel: true,
              };
            }
          }

          if (!hasPermissions(['CHANGE_ASSIGNMENT_ACTION'])) {
            if (field === 'disabled') {
              return {
                headerName: 'Action',
                field: 'disabled',
                hide: true,
              };
            }
          }

          return {
            colId: field,
            headerName,
            field,
            sortable,
            width,
            filter,
            tooltipField: field,
            cellRendererSelector,
            cellRenderer,
            hide,
            valueGetter,
            suppressFiltersToolPanel,
            suppressColumnsToolPanel,
            filterParams: filterParamsTemp,
            enableRowGroup,
            // hide: appliedFilter?.columns ? !appliedFilter?.columns.includes(field) : false,
          };
        }
      ),
    [assignmentTypes]
  );

  // handling bulk Toggle Action
  const onBulkAction = async (event: React.MouseEvent<HTMLElement>) => {
    // const onBulkAction = async (event: CellClickedEvent<Vehicle, Vehicle['batchNumber']>) => {
    if (toBulkActionEvent.length >= 50) {
      const isConfirmed = await confirm({
        title: 'Bulk Action Limit',
        description: '',
        confirmButtonText: 'Confirm',
        confirmBody: BulkActionLimit,
      });
    } else if (toBulkActionEvent.length > 0) {
      setBulkActionToggleOpen((previousOpen) => !previousOpen);
    } else {
      const isConfirmed = await confirm({
        title: 'Change Assignment Type',
        description: '',
        confirmButtonText: 'Confirm',
        confirmBody: BulkActionWarning,
      });
    }
  };

  // handling toggle panel
  const onToggleToolPanelClick = useCallback((panelId: 'columns' | 'filters') => {
    console.log('called .......', panelId);
    if (gridRef.current?.api.getOpenedToolPanel() === panelId) {
      gridRef.current?.api.closeToolPanel();
    } else {
      gridRef.current?.api.openToolPanel(panelId);
    }
  }, []);
  // handling filter change
  const onFilterChanged = (event: any) => {
    console.log('Filter Changed');
    setFilterModel(event.api.getFilterModel());
  };

  // toggleButton
  const handleChangeType = async () => {
    const isConfirmed = await confirm({
      title: 'Change Assignment Type',
      description: '',
      confirmButtonText: 'Confirm',
      confirmBody: ActionInputModal,
    });
    if (isConfirmed) {
      setDecisionPending(true);
      const testPldPayload = toBulkActionEvent.map((data: any) => ({
        previousCategory: data.category,
        previousSubCategory: data.subCategory,
        VIN: data.vin,
        batchNumber: data.batchNumber,
      }));

      testPldPayload.forEach((ele: any) => {
        if (selectedCategory !== 'Select  Category ' && selectedCategory !== '') {
          const [first, ...rest] = selectedCategory.split('-');
          const re = rest.join('-');
          // eslint-disable-next-line prefer-destructuring
          ele.newCategory = first?.trim();
          // eslint-disable-next-line prefer-destructuring
          ele.newSubCategory = re?.trim();
        }
      });

      try {
        const BulkActionPayload = {
          bulk: true,
          requestor: GLOBAL_USER,
          comments: bulkComment,
          data: testPldPayload,
        };
        const res = await updateAssignmentType(BulkActionPayload);

        if (res?.data?.failedUpdates && res?.data?.failedUpdates.length > 0) {
          tempFailedUpdates = res?.data?.failedUpdates.map((x: any) => {
            return x.batchNumber;
          });

          setFailedUpdates(tempFailedUpdates);
        }
        if (res.statusCode !== 200) {
          if (!res) {
            showErrorSnackbar({ message: 'Something went wrong. Try again.' });
          } else {
            showErrorSnackbar({ message: res.message });
          }
          return;
        }
        if (res.statusCode === 200 && res.data?.failedUpdates?.length > 0) {
          showErrorSnackbar({ message: 'Bulk update failed for selected records' });
          const newSelectedRows = rowData.filter((newData: any) => toBulkActionEvent.includes(newData));
          newSelectedRows.forEach((ele: any) => {
            if (tempFailedUpdates?.includes(ele.batchNumber)) {
              ele.failedUpdates = tempFailedUpdates;
            }
          });
        } else if (
          res.statusCode === 200 &&
          res.data?.successfulUpdates?.length > 0 &&
          res.data?.failedUpdates?.length === 0
        ) {
          showSuccessSnackbar({
            message: `The request has been successfully updated.`,
          });
        }
        gridRef.current?.api.deselectAll();
        // getAllVehicles(50, 1).then((freshRowData) => {
        //   setRowData(freshRowData);
        // });
        const newSelectedRows = rowData?.filter((newData: any) => toBulkActionEvent.includes(newData));
        const test = newSelectedRows?.forEach((ele: any) => {
          if (selectedCategory !== 'Select  Category' && selectedCategory !== '') {
            if (!tempFailedUpdates?.includes(ele.batchNumber)) {
              if (ele.prevAssgnChange) {
                if (ele.prevAssgnChange.previousCategory) {
                  ele.prevAssgnChange.previousCategory = ele.category;
                }
                if (ele.prevAssgnChange.previousSubCategory) {
                  ele.prevAssgnChange.previousSubCategory = ele.subCategory;
                }
                if (ele.prevAssgnChange.comments) {
                  ele.prevAssgnChange.comments = bulkComment;
                }
              }

              // eslint-disable-next-line prefer-destructuring
              const [first, ...rest] = selectedCategory.split('-');
              const re = rest.join('-');
              ele.category = first;
              // eslint-disable-next-line prefer-destructuring
              ele.subCategory = re;

              ele.failedUpdates = [];
            } else if (tempFailedUpdates?.includes(ele.batchNumber)) {
              ele.failedUpdates = tempFailedUpdates;
            }
          }
        });

        gridRef.current!.api.applyServerSideTransaction({
          update: newSelectedRows,
        });
        refreshGrid();
      } catch (error) {
        showErrorSnackbar({ message: 'Something went wrong. Try again.' });
      } finally {
        setDecisionPending(false);
        reason = '';
      }
    } else {
      reason = '';
    }
    setBulkActionToggleOpen(false);
  };

  const onCellClicked = useCallback((event: CellClickedEvent) => {
    console.log('.**** event.data...onCellClicked..');
    if (event.colDef.headerName === 'Action') {
      setToActionEvent([event.data]);
    }
  }, []);

  useEffect(() => {
    if (toActionEvent.length || bulkActionToggleOpen) {
      (async () => {
        let options: any = [];

        if (toBulkActionEvent.length > 0 && bulkActionToggleOpen) {
          options = toBulkActionEvent.map((action: any) => ({
            category: action.category,
            subCategory: action.subCategory,
            conditional_field_slug: 'vehicleSubStatus',
            conditional_field_value: action.vehicleSubStatus,
          }));
        }
        if (toActionEvent.length > 0 && !bulkActionToggleOpen) {
          options = toActionEvent.map((action: any) => ({
            category: action.category,
            subCategory: action.subCategory,
            conditional_field_slug: 'vehicleSubStatus',
            conditional_field_value: action.vehicleSubStatus,
          }));
        }

        const params = [
          {
            field: 'change_assignment_type',
            options,
          },
        ];
        const assignments = await getAssignmentTypesCustomFieldDetails(params);
        setAssignmentTypes(assignments);
      })();
    }
  }, [bulkActionToggleOpen, toActionEvent]);

  useEffect(() => {
    if (groupBy) {
      gridApi?.columnApi?.setRowGroupColumns([groupBy]);
    }
  }, [groupBy]);

  const onColumnRowGroupChanged = (event: any) => {
    if (event.columns && event.columns.slice(-1)[0]?.rowGroupActive) {
      setGroupBy(event.columns.slice(-1)[0]?.colId);
    } else if (event.columns.slice(-1)[0]?.rowGroupActive === false) {
      setGroupBy('');
    }
  };

  const isRowSelectable = useMemo<any>(() => {
    return (params: RowNode<any>) => {
      return !!params.data && params.data.disabled === 'no';
    };
  }, []);

  const getRowId = useCallback(function (params: GetRowIdParams) {
    return params.data._id;
  }, []);

  useEffect(() => {
    if (gridApi && bulkActionButtonVisibility) {
      onGridReady(gridApi);
    }
  }, [inputText]);

  const rowSelection = 'multiple';

  const onSelectionChanged = (event: SelectionChangedEvent) => {
    const rowDataCheck: any = [];
    gridApi.api.forEachNode((node: any) => {
      if (node.selected && rowDataCheck.length < 50) {
        rowDataCheck.push(node.data);
      }
      // eslint-disable-next-line no-useless-return
      return;
    });
    setToBulkActionEvent(rowDataCheck);
  };

  const onPaginationChanged = (event: any) => {
    if (event.newPage) {
      gridApi?.api.deselectAll();
    }
  };

  if (!columnDefs?.length) return <VehicleListLoader />;
  return (
    <>
      <LoadingDialogue open={decisionPending} />
      <Box className="top-section">
        <GridTopActions
          // onSearch={onSearch}
          filteredVins={filteredVins}
          setInputText={setInputText}
          inputText={inputText}
          setFilteredVins={setFilteredVins}
          onToggleToolPanelClick={onToggleToolPanelClick}
          showBulkActionButton={bulkActionButtonVisibility}
          onBulkAction={onBulkAction}
          bulkToggleOpen={bulkActionToggleOpen}
          handleChangeType={handleChangeType}
          bulkRef={BulkAnchorRef}
          paginationInfo={paginationInfo}
          uiHeadings={uiHeadings}
        />
      </Box>
      <Tabs
        active={active}
        tabItems={[
          {
            label: 'All Vehicles',
            onClick() {
              setBulkActionButtonVisibility(true);
              setFilterModel([]);
              setInputText('');
              setFilteredVins([]);
              setActive(0);
              navigate('/v360-app/vehicles');
            },
            content: (
              <>
                {areFiltersApplied && (
                  <AppliedFilters filterModel={filterModel} gridRef={gridRef} columnDefs={columnDefs} />
                )}
                <Box width="100%" height={`calc(100vh - ${gridHeight})`} className="ag-theme-material">
                  <AGGridHoc gridRef={gridRef}>
                    <AgGridReact
                      {...gridOptions}
                      onGridReady={onGridReady}
                      columnDefs={columnDefs}
                      getRowId={getRowId}
                      ref={gridRef}
                      onFilterChanged={onFilterChanged}
                      onCellClicked={onCellClicked}
                      rowSelection={rowSelection}
                      isRowSelectable={isRowSelectable}
                      onSelectionChanged={onSelectionChanged}
                      pagination
                      rowModelType="serverSide"
                      // eslint-disable-next-line object-shorthand
                      rowClassRules={rowClassRules}
                      cacheBlockSize={perPage}
                      paginationPageSize={perPage}
                      paginateChildRows
                      maxBlocksInCache={1}
                      onColumnVisible={(e) => setHeadings(gridApi?.api?.getColumnDefs())}
                      // onColumnVisible={}
                      // maxConcurrentDatasourceRequests={1}
                      onColumnRowGroupChanged={onColumnRowGroupChanged}
                      onPaginationChanged={onPaginationChanged}
                    />
                  </AGGridHoc>
                </Box>
              </>
            ),
          },
          {
            label: 'Change History',
            onClick() {
              setBulkActionButtonVisibility(false);
              setFilterModel([]);
              setInputText('');
              setFilteredVins([]);
              setActive(1);
              navigate('/v360-app/vehicles/change-history');
            },
            content: (
              <ChangeHistory
                areFiltersApplied={areFiltersApplied}
                filterModel={filterModel}
                gridRef={gridRef}
                setGridApi={setGridApi}
                gridHeight={gridHeight}
                gridOptions={gridOptions}
                onFilterChanged={onFilterChanged}
                perPage={perPage}
                quickFilterParser={quickFilterParser} // remove once change history ssrm changes are done.
                quickFilterMatcher={quickFilterMatcher} // remove once change history ssrm changes are done.
                inputText={inputText}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default VehiclesGrid;
